import React, { useMemo, useCallback } from 'react';

// Constants for better maintainability
const DEFAULT_FINANCE_DATA = {
  totalEarnings: 0,
  paidAmount: 0,
  pendingAmount: 0,
  recentTransactions: [],
  earningsByMonth: []
};

const Earnings = ({
  financeData,
  loading,
  earningsError,
  formatCurrency,
  formatTransactionDate,
  getTransactionStatusClass
}) => {
  // Memoized data transformation
  const transformFinanceData = useCallback((data) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return DEFAULT_FINANCE_DATA;
    }

    const apiData = data[0];
    
    return {
      totalEarnings: parseFloat(apiData.total_earnings) || 0,
      paidAmount: (parseFloat(apiData.total_amount) - parseFloat(apiData.total_deduction)) || 0,
      pendingAmount: parseFloat(apiData.total_deduction) || 0,
      recentTransactions: apiData.recentTransactions || [],
      earningsByMonth: apiData.earningsByMonth || []
    };
  }, []);

  const transformedData = useMemo(() => 
    transformFinanceData(financeData?.data),
    [financeData?.data, transformFinanceData]
  );

  // Memoized helper components
  const EarningsCard = useCallback(({ title, amount, label, type }) => (
    <div className={`earnings-card ${type}`}>
      <div className="card-content">
        <h3>{title}</h3>
        <div className="amount">{formatCurrency(amount)}</div>
        <div className="card-label">{label}</div>
      </div>
    </div>
  ), [formatCurrency]);

  const TransactionItem = useCallback(({ transaction }) => (
    <div className="transaction-item">
      <div className="transaction-info">
        <div className="transaction-main">
          <span className="transaction-booking">
            #{transaction.booking_id || 'N/A'}
          </span>
          <span className="transaction-customer">
            {transaction.customer_name}
          </span>
        </div>
        <div className="transaction-package">
          {transaction.package_name}
        </div>
        <div className="transaction-date">
          {formatTransactionDate(transaction.date)}
        </div>
      </div>
      <div className="transaction-amount">
        <div className="amount">{formatCurrency(transaction.amount)}</div>
        <span className={`status-badge ${getTransactionStatusClass(transaction.status)}`}>
          {transaction.status}
        </span>
      </div>
    </div>
  ), [formatCurrency, formatTransactionDate, getTransactionStatusClass]);

  const ChartBar = useCallback(({ monthData, maxEarnings, index }) => {
    const percentage = maxEarnings > 0 ? (monthData.earnings / maxEarnings) * 100 : 0;
    
    return (
      <div key={index} className="chart-bar">
        <div className="bar-label">{formatCurrency(monthData.earnings)}</div>
        <div 
          className="bar-fill" 
          style={{ height: `${percentage}%` }}
        ></div>
        <div className="bar-month">{monthData.month}</div>
      </div>
    );
  }, [formatCurrency]);

  // Memoized render sections
  const renderEarningsCards = useMemo(() => {
    const cardsConfig = [
      {
        type: 'total-earnings',
        title: 'Total Earnings',
        amount: transformedData.totalEarnings,
        label: 'Lifetime earnings'
      },
      {
        type: 'paid-earnings',
        title: 'Paid Amount',
        amount: transformedData.paidAmount,
        label: 'Received payments'
      },
      {
        type: 'pending-earnings',
        title: 'Pending Amount',
        amount: transformedData.pendingAmount,
        label: 'Awaiting clearance'
      }
    ];

    return cardsConfig.map((card) => (
      <EarningsCard
        key={card.type}
        type={card.type}
        title={card.title}
        amount={card.amount}
        label={card.label}
      />
    ));
  }, [transformedData, EarningsCard]);

  const renderTransactions = useMemo(() => {
    if (transformedData.recentTransactions.length === 0) {
      return (
        <div className="no-transactions">
          <p>No transactions found</p>
          <small>Transaction data will appear here when you have bookings</small>
        </div>
      );
    }

    return (
      <div className="transactions-list">
        {transformedData.recentTransactions.map((transaction, index) => (
          <TransactionItem
            key={transaction.id || index}
            transaction={transaction}
          />
        ))}
      </div>
    );
  }, [transformedData.recentTransactions, TransactionItem]);

  const renderEarningsChart = useMemo(() => {
    if (transformedData.earningsByMonth.length === 0) {
      return (
        <div className="no-chart-data">
          <p>No earnings data available for chart</p>
          <small>Monthly earnings data will appear over time</small>
        </div>
      );
    }

    const maxEarnings = Math.max(...transformedData.earningsByMonth.map(m => m.earnings));

    return (
      <div className="monthly-earnings-chart">
        {transformedData.earningsByMonth.map((monthData, index) => (
          <ChartBar
            key={index}
            monthData={monthData}
            maxEarnings={maxEarnings}
            index={index}
          />
        ))}
      </div>
    );
  }, [transformedData.earningsByMonth, ChartBar]);

  const renderLoadingState = useMemo(() => (
    <div className="loading-earnings" aria-live="polite">
      <div className="spinner"></div>
      <p>Loading earnings data...</p>
    </div>
  ), []);

  const renderErrorState = useMemo(() => (
    <div className="error-earnings" aria-live="polite">
      <div className="error-icon">❌</div>
      <p>Error loading earnings data: {earningsError}</p>
    </div>
  ), [earningsError]);

  const renderEarningsContent = useMemo(() => (
    <div className="earnings-section">
      <div className="earnings-header">
        <h2>Earnings & Finance</h2>
        <div className="earnings-overview-cards">
          {renderEarningsCards}
        </div>
      </div>

      <div className="earnings-content">
        <div className="recent-transactions">
          <h3>Recent Transactions</h3>
          {renderTransactions}
        </div>

        <div className="earnings-chart">
          <h3>Earnings Overview</h3>
          <div className="chart-container">
            {renderEarningsChart}
          </div>
        </div>
      </div>
    </div>
  ), [renderEarningsCards, renderTransactions, renderEarningsChart]);

  // Main render function
  const renderEarningsSection = useMemo(() => {
    if (loading.earnings) {
      return renderLoadingState;
    }

    if (earningsError) {
      return renderErrorState;
    }

    return renderEarningsContent;
  }, [loading.earnings, earningsError, renderLoadingState, renderErrorState, renderEarningsContent]);

  return renderEarningsSection;
};

export default React.memo(Earnings);